/**
 * In-app browser (webview) detection.
 *
 * Social/messaging apps open external links in an embedded webview instead of
 * the system browser. In those environments two things break that surfaces we
 * own depend on:
 *
 *   1. OAuth popups (Firebase `signInWithPopup` for Google / Facebook). Google
 *      blocks OAuth in embedded webviews outright, and iOS partitions the
 *      `firebaseapp.com` auth-handler's `sessionStorage`, so the redirect
 *      round-trip dies with "Unable to process request due to missing initial
 *      state" — the exact failure renters hit after tapping a WhatsApp
 *      notification link.
 *   2. `getUserMedia` (the KYC in-browser camera). See `useCameraCapability`.
 *
 * SpotMe gets meaningful paid (FB/IG ads) and notification (WhatsApp templates)
 * traffic from these webviews, so auth surfaces must detect them and steer to
 * phone / email instead.
 */

/**
 * User-agent tokens for known in-app browsers. Kept conservative — every token
 * here is app-specific, so it never matches a real system browser (no
 * false-positives that would hide social login from a legitimate Safari /
 * Chrome user).
 */
export const IN_APP_BROWSER_TOKENS = [
  'FBAN', // Facebook iOS
  'FBAV', // Facebook iOS
  'FB_IAB', // Facebook Android
  'Instagram',
  'Bytedance', // TikTok
  'musical_ly', // TikTok (legacy)
  'Line/', // LINE
  'MicroMessenger', // WeChat
  'Twitter', // X / Twitter in-app
  'WhatsApp', // WhatsApp Android (some builds tag the UA)
  'GSA/' // Google Search App in-app browser
]

/**
 * WhatsApp's iOS in-app browser is a bare `WKWebView`: its UA carries
 * `AppleWebKit` + `Mobile` but NO `Safari` token, and none of the app-specific
 * tokens above. Real iOS browsers always identify themselves — Safari keeps the
 * `Safari` token; Chrome/Firefox/Edge use `CriOS`/`FxiOS`/`EdgiOS` — so the
 * absence of all of them is a reliable webview signature.
 */
const isIosWebView = (ua: string): boolean =>
  /iPhone|iPod|iPad/.test(ua) &&
  /AppleWebKit/.test(ua) &&
  !/Safari/.test(ua) &&
  !/CriOS|FxiOS|EdgiOS/.test(ua)

/** Android System WebView tags its UA with `; wv)` (real Chrome does not). */
const isAndroidWebView = (ua: string): boolean =>
  /Android/.test(ua) && /;\s*wv\)/.test(ua)

/**
 * True when the given user-agent belongs to a known in-app browser (webview).
 * Combines the explicit app-token list with the iOS/Android webview signatures.
 * SSR-safe: returns `false` for an empty/undefined UA.
 */
export const isInAppBrowser = (
  userAgent: string | undefined | null
): boolean => {
  if (!userAgent) return false
  const hasToken = IN_APP_BROWSER_TOKENS.some((token) =>
    userAgent.includes(token)
  )
  return hasToken || isIosWebView(userAgent) || isAndroidWebView(userAgent)
}

/**
 * Reads the current browser's UA and reports whether it's an in-app browser.
 * Returns `false` during SSR (no `navigator`). Callers that render should still
 * mount-gate the result in state to avoid a hydration mismatch.
 */
export const detectInAppBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false
  return isInAppBrowser(navigator.userAgent)
}
